
import { useState, useCallback } from 'react';
import { sanitizeInput, validateUrl } from '@/utils/security';
import { toast } from 'sonner';

interface UseSecureInputOptions {
  maxLength?: number;
  validateUrls?: boolean;
  required?: boolean;
}

export const useSecureInput = (
  initialValue: string = '',
  options: UseSecureInputOptions = {}
) => {
  const { maxLength = 1000, validateUrls = false, required = false } = options;
  const [value, setValue] = useState(sanitizeInput(initialValue, maxLength));
  const [error, setError] = useState<string | null>(null);

  const handleChange = useCallback((newValue: string) => {
    // Sanitize input
    const sanitized = sanitizeInput(newValue, maxLength);
    
    // Validate if required
    if (required && !sanitized.trim()) {
      setError('This field is required');
      setValue(sanitized);
      return;
    }
    
    // Validate URLs if needed
    if (validateUrls && sanitized && !validateUrl(sanitized)) {
      setError('Please enter a valid URL from an allowed domain');
      setValue(sanitized);
      return;
    }
    
    setError(null);
    setValue(sanitized);
  }, [maxLength, validateUrls, required]);

  const validate = useCallback((): boolean => {
    if (required && !value.trim()) {
      setError('This field is required');
      return false;
    }
    
    if (validateUrls && value && !validateUrl(value)) {
      setError('Please enter a valid URL from an allowed domain');
      return false;
    }
    
    return true;
  }, [value, required, validateUrls]);

  return {
    value,
    error,
    setValue: handleChange,
    validate,
    isValid: !error && (!required || value.trim())
  };
};
